import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit, MapPin, Percent, CheckCircle, XCircle, Calendar, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import useRegionStore from '@/stores/regionStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'

const RegionDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    currentRegion,
    loading,
    error,
    fetchRegionById,
    clearError,
    clearCurrentRegion
  } = useRegionStore()

  useEffect(() => {
    if (id) {
      fetchRegionById(id)
    }

    return () => {
      clearCurrentRegion()
    }
  }, [id])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (enabled: boolean) => {
    return enabled ? (
      <Badge variant="default" className="flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        Actif
      </Badge>
    ) : (
      <Badge variant="destructive" className="flex items-center gap-1">
        <XCircle className="h-3 w-3" />
        Inactif
      </Badge>
    )
  }

  const getTypeBadge = (region: any) => {
    const typeCode = region?.type_code || region?.type_id
    const typeName = region?.type_name || typeCode
    
    const typeMap: Record<string, string> = {
      'state': 'État',
      'province': 'Province',
      'territory': 'Territoire',
      'region': 'Région'
    }
    const colorMap: Record<string, string> = {
      'state': 'bg-blue-100 text-blue-800',
      'province': 'bg-green-100 text-green-800',
      'territory': 'bg-purple-100 text-purple-800',
      'region': 'bg-orange-100 text-orange-800'
    }
    
    const displayName = typeMap[typeCode] || typeName || typeCode || 'Inconnu'
    const colorClass = colorMap[typeCode] || 'bg-gray-100 text-gray-800'
    
    return (
      <Badge variant="outline" className={colorClass}>
        {displayName}
      </Badge>
    )
  }

  if (loading && !currentRegion) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!currentRegion && !loading) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Région non trouvée"
          subtitle="La région que vous recherchez n'existe pas ou a été supprimée."
          breadcrumbs={[
            { label: 'Régions', href: '/regions' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/regions"
        />
        <Alert>
          <AlertDescription>
            Cette région n'a pas pu être chargée. Elle a peut-être été supprimée ou vous n'avez pas les permissions nécessaires.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!currentRegion) {
    return null
  }

  const breadcrumbs = [
    { label: 'Régions', href: '/regions' },
    { label: currentRegion.name, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: () => navigate(`/regions/${id}/edit`),
      variant: 'default' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title={currentRegion.name}
        subtitle={`Informations détaillées sur la région ${currentRegion.name}`}
        breadcrumbs={breadcrumbs}
        backUrl="/regions"
        actions={actions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Informations générales" subtitle="Détails de base et configuration de la région">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-full">
                <MapPin className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{currentRegion.name}</h2>
                <p className="text-muted-foreground">
                  Code: {currentRegion.code} • Type: {getTypeBadge(currentRegion)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Statut</h3>
                  <div className="mt-1">
                    {getStatusBadge(currentRegion.enabled)}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Type de région</h3>
                  <p className="mt-1 text-lg font-medium">
                    {getTypeBadge(currentRegion)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Classification administrative de la région
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Pays associé</h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <Globe className="h-5 w-5 text-muted-foreground" />
                    <span className="text-lg font-medium">
                      ID: {currentRegion.country_id}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Identifiant du pays auquel cette région appartient
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Taux de taxe</h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <Percent className="h-5 w-5 text-muted-foreground" />
                    <span className="text-lg font-medium">
                      {currentRegion.tax_rate}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Taux de taxe applicable dans cette région
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Exemple de calcul</h3>
                  <div className="mt-2 p-3 bg-muted rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Montant HT:</span>
                      <span className="font-medium">100.00 €</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm">Taxe ({currentRegion.tax_rate}%):</span>
                      <span className="font-medium">{(100 * (currentRegion.tax_rate || 0) / 100).toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t">
                      <span className="text-sm font-medium">Montant TTC:</span>
                      <span className="font-bold">
                        {(100 + (100 * (currentRegion.tax_rate || 0) / 100)).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>
        </div>

        {/* Métadonnées et actions */}
        <div className="space-y-6">
          <DetailSection title="Métadonnées" subtitle="Informations techniques et dates">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Identifiant</h3>
                <p className="mt-1 font-mono text-sm bg-muted p-2 rounded">
                  {currentRegion.id}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Créé le</h3>
                    <p className="mt-1">
                      {formatDate(currentRegion.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Dernière modification</h3>
                    <p className="mt-1">
                      {formatDate(currentRegion.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate(`/regions/${id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Modifier cette région
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/regions')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à la liste
              </Button>
            </div>
          </DetailSection>

          <DetailSection title="Statistiques">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Utilisation dans le système</span>
                <Badge variant="outline">À implémenter</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Taxes associées</span>
                <Badge variant="outline">À implémenter</Badge>
              </div>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default RegionDetails
